import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { SaveIcon } from 'lucide-react'

import { prepareExpirationDate } from '@/utils/helpers'

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/newRedesign'
import { Button } from '@/components/newRedesign'
import { Form } from '@/components/ui/form'
import {
  FormSearchableSelect,
  FormInput,
  FormDatePicker,
  FormSelect,
  FormTextarea,
} from '@/components/newRedesign/form'
import type { SearchableSelectOption } from '@/components/newRedesign'
import { getProducts } from '@/api/productsAPI'
import { getSuppliers } from '@/api/providersAPI'
import { getCurrencies } from '@/api/currenciesAPI'
import { format, parseISO, isValid } from 'date-fns'
import { cn } from '@/lib/utils'

interface Product {
  id: number
  name: string
}

interface Supplier {
  id: number
  name: string
}

export interface BatchFormData {
  id?: number
  productId: number
  expirationAt: string | Date
  entryQuantity: number
  noPackages?: number
  supplierId: number
  registeredPrice: {
    amount: number | undefined
    codeCurrency: string
  }
  description?: string
}

interface BatchDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSave: (_data: BatchFormData) => void
  onCancel: () => void
  isPending?: boolean
  mode?: 'create' | 'edit'
  initialData?: Partial<BatchFormData>
  defaultProduct?: Product
  defaultSupplier?: Supplier
}

export function BatchDialog({
  open,
  onOpenChange,
  onSave,
  isPending = false,
  mode = 'create',
  initialData,
  defaultProduct,
  defaultSupplier,
}: BatchDialogProps) {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  )

  const form = useForm<BatchFormData>({
    defaultValues: {
      productId: undefined,
      expirationAt: '',
      entryQuantity: undefined,
      noPackages: undefined,
      supplierId: undefined,
      registeredPrice: {
        amount: undefined,
        codeCurrency: '',
      },
      description: '',
    },
  })

  // Fetcher para productos
  const fetchProducts = async (
    search: string
  ): Promise<SearchableSelectOption[]> => {
    const response = await getProducts({
      page: 1,
      per_page: 10,
      search,
      //type: 'STOCK,RAW,MANUFACTURED,INPUT,ASSET,VARIATION',
      type: 'STOCK',
    })
    if (!Array.isArray(response)) return []
    return response.map(({ id, name }) => ({
      id,
      label: name,
    }))
  }

  // Fetcher para proveedores
  const fetchSuppliers = async (
    search: string
  ): Promise<SearchableSelectOption[]> => {
    const response = await getSuppliers({
      page: 1,
      per_page: 10,
      search,
    })
    if (!Array.isArray(response)) return []
    return response.map((supplier: Supplier) => ({
      id: supplier.id,
      label: supplier.name,
    }))
  }

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
    staleTime: 5 * 60 * 1000,
  })

  // Update form when dialog opens
  useEffect(() => {
    if (open && !isPending) {
      if (mode === 'edit' && initialData) {
        // Load data for editing
        let normalizedExpirationAt = ''
        if (initialData.expirationAt) {
          if (typeof initialData.expirationAt === 'string') {
            const parsedDate = parseISO(initialData.expirationAt)
            normalizedExpirationAt = isValid(parsedDate)
              ? format(parsedDate, 'yyyy-MM-dd')
              : ''
          } else if (initialData.expirationAt instanceof Date) {
            normalizedExpirationAt = isValid(initialData.expirationAt)
              ? format(initialData.expirationAt, 'yyyy-MM-dd')
              : ''
          }
        }

        // Set selected values first - handle both direct values and nested objects
        const productToSet = defaultProduct || null
        const supplierToSet = defaultSupplier || null

        setSelectedProduct(productToSet)
        setSelectedSupplier(supplierToSet)

        // Then reset form with all data
        setTimeout(() => {
          form.reset({
            id: initialData.id,
            productId: initialData.productId || undefined,
            expirationAt: normalizedExpirationAt,
            entryQuantity: initialData.entryQuantity || undefined,
            noPackages: initialData.noPackages ?? undefined,
            supplierId: initialData.supplierId || undefined,
            registeredPrice: {
              amount: initialData.registeredPrice?.amount ?? undefined,
              codeCurrency: initialData.registeredPrice?.codeCurrency || '',
            },
            description: initialData.description || '',
          })
        }, 50)
      } else {
        // Reset to empty values for creating new batch
        setSelectedProduct(null)
        setSelectedSupplier(null)
        form.reset({
          productId: undefined,
          expirationAt: '',
          entryQuantity: undefined,
          noPackages: undefined,
          supplierId: undefined,
          registeredPrice: {
            amount: undefined,
            codeCurrency: '',
          },
          description: '',
        })
      }
    }
  }, [
    open,
    mode,
    initialData,
    defaultProduct,
    defaultSupplier,
    isPending,
    form,
  ])

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      form.reset()
      setSelectedProduct(null)
      setSelectedSupplier(null)
    }
  }, [open, form])

  const handleSubmit = (data: BatchFormData) => {
    // Prepare expiration date using helper (sets time to 04:00)
    const expirationAt = prepareExpirationDate(data.expirationAt)

    onSave({
      ...data,
      expirationAt: expirationAt || '',
      ...(mode === 'edit' && initialData?.id && { id: initialData.id }),
    })
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={cn(
          'flex max-h-[100dvh] w-full max-w-[95vw] flex-col overflow-hidden bg-[#FAFAFA] p-0',
          'sm:max-h-[85vh] sm:max-w-[90vw] md:max-w-[800px]'
        )}
      >
        {/* Dialog Header */}
        <DialogHeader
          className={cn(
            'flex h-[52px] shrink-0 flex-row items-center justify-between',
            'border-b border-[#D4D4D4] px-4 py-0'
          )}
        >
          <DialogTitle className="text-xl font-semibold leading-[24px] tracking-[-0.02em] text-[#262626]">
            {mode === 'edit' ? 'Editar Lote' : 'Nuevo Lote'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div
            className={cn(
              'flex max-h-[calc(100dvh-120px)] flex-1 flex-col gap-4',
              'overflow-y-auto bg-[#FAFAFA] px-4 py-4',
              'sm:max-h-[calc(85vh-120px)] sm:gap-4 sm:px-4 sm:py-4'
            )}
          >
            {/* Product Selection */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Producto <span className="text-[#262626]">(*)</span>
              </label>
              <FormSearchableSelect
                control={form.control}
                defaultValue={selectedProduct}
                disabled={mode === 'edit'}
                emptyMessage="No se encontraron productos"
                fetcher={fetchProducts}
                name="productId"
                placeholder="Seleccionar"
                rules={{
                  required: 'El producto es requerido',
                }}
                searchPlaceholder="Buscar producto..."
                showLabel={false}
              />
            </div>

            {/* Expiration Date */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Fecha de expiración <span className="text-[#262626]">(*)</span>
              </label>
              <FormDatePicker
                control={form.control}
                minDate={new Date()}
                name="expirationAt"
                placeholder="Fecha"
                rules={{
                  required: 'La fecha de expiración es requerida',
                }}
                showLabel={false}
              />
            </div>

            {/* Quantity and Package Number Row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Quantity */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                  Cantidad <span className="text-[#262626]">(*)</span>
                </label>
                <FormInput
                  control={form.control}
                  inputSize="md"
                  name="entryQuantity"
                  placeholder="Cantidad de entrada "
                  rules={{
                    required: 'La cantidad es requerida',
                    validate: (value: number) => {
                      if (value <= 0) {
                        return 'La cantidad debe ser mayor a 0'
                      }
                      return true
                    },
                  }}
                  showLabel={false}
                  type="number"
                />
              </div>

              {/* Number of Packages */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                  Número de paquetes
                </label>
                <FormInput
                  control={form.control}
                  inputSize="md"
                  name="noPackages"
                  placeholder="Número de paquetes"
                  showLabel={false}
                  type="number"
                />
              </div>
            </div>

            {/* Supplier, Registered Price and Currency Row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              {/* Supplier - más grande (5 columnas) */}
              <div className="flex flex-col gap-2 sm:col-span-5">
                <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                  Proveedor <span className="text-[#262626]">(*)</span>
                </label>
                <FormSearchableSelect
                  control={form.control}
                  defaultValue={selectedSupplier}
                  emptyMessage="No se encontraron proveedores"
                  fetcher={fetchSuppliers}
                  name="supplierId"
                  placeholder="Seleccionar"
                  rules={{
                    required: 'El proveedor es requerido',
                  }}
                  searchPlaceholder="Buscar proveedor..."
                  showLabel={false}
                />
              </div>

              {/* Registered Price - mediano (4 columnas) */}
              <div className="flex flex-col gap-2 sm:col-span-4">
                <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                  Precio registrado <span className="text-[#262626]">(*)</span>
                </label>
                <FormInput
                  control={form.control}
                  inputSize="md"
                  name="registeredPrice.amount"
                  placeholder="0.00"
                  rules={{
                    required: 'El precio es requerido',
                    validate: (value: number) => {
                      if (value <= 0) {
                        return 'El precio debe ser mayor a 0'
                      }
                      return true
                    },
                  }}
                  showLabel={false}
                  type="number"
                />
              </div>

              {/* Currency - más pequeño (3 columnas) */}
              <div className="flex flex-col gap-2 sm:col-span-3">
                <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                  Moneda <span className="text-[#262626]">(*)</span>
                </label>
                <FormSelect
                  control={form.control}
                  data={
                    currencies?.map(({ code }) => ({
                      id: code,
                      title: code,
                    })) || []
                  }
                  name="registeredPrice.codeCurrency"
                  placeholder="Moneda"
                  rules={{
                    required: 'La moneda es requerida',
                  }}
                  showLabel={false}
                />
              </div>
            </div>

            {/* Observations */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Observaciones
              </label>
              <FormTextarea
                control={form.control}
                name="description"
                placeholder="Observaciones"
                rows={3}
                showLabel={false}
              />
            </div>
          </div>

          {/* Dialog Footer */}
          <DialogFooter
            className={cn(
              'flex h-auto shrink-0 items-center justify-end',
              'border-t border-[#D4D4D4] px-3 py-3',
              'sm:h-[68px] sm:px-4 sm:py-0'
            )}
          >
            <Button
              className={cn(
                'flex h-9 w-full items-center justify-center',
                'gap-2 rounded-md bg-[#1E293B] px-4',
                'hover:bg-[#334155]',
                'sm:w-auto sm:min-w-[110px]'
              )}
              disabled={isPending}
              icon={<SaveIcon className="h-4 w-4" />}
              onClick={form.handleSubmit(handleSubmit)}
              type="button"
              variant="default"
            >
              {isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
